import { BIG_NUMBER, SMALL_NUMBER } from '../../constants/render-constants';
import threeShim from '../../shim/three';

const { Matrix4, Vector3 } = threeShim;

const getMatrixFromProperties = ({ properties }) => {
    const { angle, x, y } = properties;

    let theta = angle;

    const matrixToReturn = new Matrix4();

    if (theta) {
        theta = (theta * Math.PI) / 180;
        matrixToReturn.makeRotationZ(theta);
    }

    matrixToReturn.setPosition(new Vector3(x, y, 0));
    return matrixToReturn;
};

const applyMatrixToPoint = ({ matrix, p }) => {
    const v = new Vector3(p.x, p.y, 0);

    v.applyMatrix4(matrix);
    return {
        x: v.x,
        y: v.y,
    };
};

const getCenterFromBoundingBox = (box) => ({
    x: (box.min.x + box.max.x) * 0.5,
    y: (box.min.y + box.max.y) * 0.5,
});

const getDimensionsFromBoundingBox = (box) => {
    const width = box.max.x - box.min.x;
    const height = box.max.y - box.min.y;

    return {
        height,
        width,
    };
};
const getDistance = ({ p1, p2 }) => {
    const dx = p1.x - p2.x;
    const dy = p1.y - p2.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    return dist;
};

const addMarginToBoundingBox = ({ boundingBox, margin }) => {
    const center = {
        x: (boundingBox.max.x + boundingBox.min.x) * 0.5,
        y: (boundingBox.max.y + boundingBox.min.y) * 0.5,
    };
    let width = boundingBox.max.x - boundingBox.min.x;
    let height = boundingBox.max.y - boundingBox.min.y;

    width *= 1 + margin;
    height *= 1 + margin;

    const newBoundingBox = {
        min: {
            x: center.x - width * 0.5,
            y: center.y - height * 0.5,
        },
        max: {
            x: center.x + width * 0.5,
            y: center.y + height * 0.5,
        },
    };

    return newBoundingBox;
};

const getBoxFromPositions = ({ positions }) => {
    const min = {
        x: BIG_NUMBER,
        y: BIG_NUMBER,
    };

    const max = {
        x: SMALL_NUMBER,
        y: SMALL_NUMBER,
    };

    for (let i = 0; i < positions.length; i++) {
        const position = positions[i];
        const { x, y, bbox } = position;

        if (bbox) {
            max.x = Math.max(max.x, bbox.max.x);
            max.y = Math.max(max.y, bbox.max.y);
            min.x = Math.min(min.x, bbox.min.x);
            min.y = Math.min(min.y, bbox.min.y);
        } else {
            max.x = Math.max(max.x, x);
            max.y = Math.max(max.y, y);
            min.x = Math.min(min.x, x);
            min.y = Math.min(min.y, y);
        }
    }

    return {
        max,
        min,
    };
};

const cloneBoundingBox = (box) => ({
    max: {
        x: box.max.x,
        y: box.max.y,
    },
    min: {
        x: box.min.x,
        y: box.min.y,
    },
});

const cartesianToParametric = ({ box, pos }) => {
    const u = (pos.x - box.min.x) / (box.max.x - box.min.x);
    const v = (pos.y - box.min.y) / (box.max.y - box.min.y);

    return {
        u,
        v,
    };
};

const parametricToCartesian = ({ box, coords }) => {
    const { u, v } = coords;
    const x = (1 - u) * box.min.x + u * box.max.x;
    const y = (1 - v) * box.min.y + v * box.max.y;

    return {
        x,
        y,
    };
};

export default {
    addMarginToBoundingBox,
    applyMatrixToPoint,
    cartesianToParametric,
    cloneBoundingBox,
    getBoxFromPositions,
    getCenterFromBoundingBox,
    getDimensionsFromBoundingBox,
    getDistance,
    getMatrixFromProperties,
    parametricToCartesian,
};
